import { getCreditCardType } from 'cleave-zen';
import Big from 'big.js';
import { DateTime } from 'luxon';
import Batch from 'smg/batch';
import { Start, Stop } from 'smg/buttons';
import * as countries from 'smg/countries';
import { Carriers } from 'smg/customer-api/carriers';
import { Shipments } from 'smg/customer-api/shipments';
import { GetMoldFees } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { OrderAggregates, Orders } from 'smg/customer-api/orders';
import { Session } from 'smg/customer-api/session';
import { MultiLoader } from 'smg/multiloader';
import * as template from 'smg/template';
import { CapitalizeFirstLetter, DollarFormat } from 'smg/util';
import { AccountDetailsInit, RequireSession } from '../../account';
import { DashboardGrid } from '../../dashboard-grid';
import { AddCartItem } from '../../carts';
import { TicketExpectedBackLine, TicketNeedByLine, TicketStatus, TicketTypeLabel, openTicketStatusQ } from '../../design-ticket-status';
import { Relref } from '../../hugo';
import i18n from '../../i18n';
import { CustomerShipmentRows, OrderStateByKey, OrderStateOf, CountOrders, InflightQ, SearchTerm } from '../../order-states';
import { ProofItem, ProofStack } from '../../proof-stack';
import { MoldIdentity, MoldLabel, OrderArt, TicketArt, TimelineLoaders, RelativeTime } from '../../timeline';

const lastSeenKey = 'plus:account-overview:lastSeen';
const inflightData =
    'id,invoiceNumber,created,orderLineItems,orderShippingOptions,orderPayments,total,balance,' +
    'orderStatusID,pending,paidByPurchaseOrder,accountState,customerShipDate,eventDate,' +
    'shipmentsToCustomerNotDeliveredCount,shipmentsCount,orderProofs';
const proofsData =
    'id,name,file,projectName,frontMoldID,backMoldID,frontMoldLatestConfirmed,backMoldLatestConfirmed,' +
    'frontMoldExpires,backMoldExpires,moldExpires,orderProofs,latestOrderLineItem';
const ticketsData =
    'id,key,created,quoteRequestProjectTaskStatusID,quoteRequestProjectTaskTypeID,quoteRequest,' +
    'quoteProof,assignedUser,lastUserEvent';
const quotesData = 'id,serial,created,key,cachedTotal,quoteLineItems,quoteSents,quoteOrders,quoteProofs';
const paymentData =
    'address,' +
    'creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID,label,accountType,bankType';
const inflightKeys = ['awaiting-payment', 'order-received', 'in-production', 'on-the-way'] as const;
const countChipOrder = ['awaiting-payment', 'order-received', 'in-production', 'on-the-way', 'delivered'] as const;
// Requires a digit after "PO" so a billing name like "Polly Smith" is never a PO chip.
const poFromName = /\bPO\s*#?\s*\d[\w./-]*/i;

interface resource<T> {
    promise: Promise<T>;
    failed: boolean;
    reload: () => Promise<T>;
}

interface region {
    $sec: HTMLElement;
    resources: resource<unknown>[];
    hideWhenEmpty?: boolean;
    render: () => Promise<boolean>;
}

interface defaults {
    payment?: models.HyejooPaymentOption;
    shipping?: models.HyejooShippingOption;
    tax: models.HyejooUserTaxExemption[];
}

interface transitShipment {
    order: models.HyejooOrder;
    shippingOption: models.OrderShippingOption;
    shipment: models.Shipment;
    index: number;
    total: number;
}

interface moldCard {
    key: string;
    proof: models.HyejooProof;
    orders: models.Order[];
    sides: moldSide[];
    soon: boolean;
}

interface moldSide {
    side: 'front' | 'back' | '';
    status: 'expires' | 'lapsed' | 'none';
    date: DateTime;
}

void main();

// quote.ts drags a heavy dependency graph, so it loads on click, not up front.
async function openNewQuote() {
    const quote = await import('../../quote');
    quote.QuoteModal({
        lineItem: '',
        type: '',
        size: '',
        productSelect: quote.AccountQuoteProducts,
        form: 'account overview',
        backButtonText: i18n('accountBackToOverview'),
    });
}

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();
    DashboardGrid(document.querySelector('.dashboard-grid-wrap') as HTMLElement);

    // "Start a quote" opens the quote form right here with a product picker
    // (like the Files page) instead of bouncing to the contact page; the href
    // stays as the no-JS fallback.
    for (const $open of document.querySelectorAll<HTMLAnchorElement>('.ov-quote-open')) {
        $open.addEventListener('click', e => {
            e.preventDefault();
            void openNewQuote();
        });
    }

    let lastSeenISO: string | undefined;
    try {
        lastSeenISO = localStorage.getItem(lastSeenKey) ?? undefined;
    } catch {
        lastSeenISO = undefined;
    }

    // Exactly the chart's window — this month plus the eleven before it — so
    // the totals and the bars describe the same orders.
    const spendSince = DateTime.now().startOf('month').minus({ months: 11 }).toISODate() ?? '';
    const since90 = DateTime.now().minus({ days: 90 }).toISODate() ?? '';
    const initial = new Batch();

    const sessionRes = resource(() => Session());
    const salesRepRes = resource(b => b.get('/sessions', { params: { data: 'salesRep' } }).
        then(resp => models.SessionGetResponse.fromJSON(resp.data).salesRep), initial);
    const lifetimeRes = resource(b => Promise.all([
        OrderAggregates('', ['id']).then(resp => resp.aggregates.get('id')?.count ?? 0),
        b.get('/quote-request-project-tasks', { params: { colStats: 'id' } }).then(resp => models.ColumnStatsResponse.fromJSON(resp.data).count),
        b.get('/v2/quotes', { params: { colStats: 'id' } }).then(resp => models.ColumnStatsResponse.fromJSON(resp.data).count),
    ]).then(([orders, tickets, quotes]) => ({ orders, tickets, quotes })), initial);
    const inflightRes = resource(b => loadInflight(b), initial);
    // In-flight counts derive from the in-flight list at render time; only
    // "delivered this year" needs its own count query. Keeping this resource
    // independent of inflightRes matters for retry: a resource whose load
    // captures another resource's promise re-fails against the OLD rejected
    // promise when both reload in the same click.
    // "Delivered this year" — by when the last shipment actually arrived.
    const deliveredYearRes = resource(() => {
        const delivered = OrderStateByKey('delivered');
        return delivered ? CountOrders(delivered, `deliveredAt:>=${DateTime.now().startOf('year').toISODate() ?? ''}`) : Promise.resolve(0);
    }, initial);
    const openTicketsRes = resource(b => drain(b, (batch, p) => batch.get('/quote-request-project-tasks', {
        params: { sort: 'created desc', max: 100, p, q: openTicketStatusQ, data: ticketsData },
    }).then(resp => {
        const data = models.QuoteRequestProjectTasksHyejooResponse.fromJSON(resp.data);
        return { rows: data.quoteRequestProjectTasks, nextPage: data.nextPage };
    })), initial);
    const proofsRes = resource(b => drain(b, (batch, p) => batch.get('/proofs', {
        params: { sort: 'moldExpires asc', max: 100, p, q: 'raster:true', data: proofsData },
    }).then(resp => {
        const data = models.ProofsHyejooResponse.fromJSON(resp.data);
        return { rows: data.proofs, nextPage: data.nextPage };
    })), initial);
    // latestSentAt, not created: a quote drafted long ago and re-sent this
    // week is recent activity (created's SORT already ranks by latest send).
    const recentQuotesRes = resource(b => drain(b, (batch, p) => batch.get('/v2/quotes', {
        params: { sort: 'created desc', max: 100, p, q: `latestSentAt:>=${since90}`, data: quotesData },
    }).then(resp => {
        const data = models.QuotesHyejooResponse.fromJSON(resp.data);
        return { rows: data.quotes, nextPage: data.nextPage };
    })), initial);
    // Settled = paid down, or PO-backed (a PO order legitimately carries a
    // balance and still counts as spend). No single server filter expresses
    // that OR, so aggregate client-side from the drained rows.
    const spendRes = resource(b => drain(b, (batch, p) => Orders({
        q: `created:>=${spendSince}`,
        data: 'total,created,balance,paidByPurchaseOrder',
        sort: 'created desc',
        max: 100,
        p,
    }).then(data => ({ rows: data.results, nextPage: data.nextPage }))).then(orders => orders.filter(order => !order.balance.gt(0.05) || order.paidByPurchaseOrder)), initial);
    const defaultsRes = resource(b => {
        const credit = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
        const bank = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK;
        const payment = b.get('/payment-options', {
            params: { q: `label:default type:${credit},${bank}`, max: 1, data: paymentData },
        }).then(resp => models.PaymentOptionsHyejooResponse.fromJSON(resp.data).paymentOptions[0]);
        const shipping = b.get('/shipping-options', {
            params: { q: 'label:default', max: 1, data: 'id,label,address' },
        }).then(resp => models.ShippingOptionsHyejooResponse.fromJSON(resp.data).shippingOptions[0]);
        const tax = b.get('/user-tax-exemptions', {
            params: { data: 'id,userTaxExemptionReason,stateID,countryID,number,default', max: 50 },
        }).then(resp => models.UserTaxExemptionsHyejooResponse.fromJSON(resp.data).userTaxExemptions);
        return Promise.all([payment, shipping, tax]).then(([p, s, t]) => ({ payment: p, shipping: s, tax: t }));
    }, initial);

    void initial.exec();

    const lifetime = await lifetimeRes.promise.then(v => v, () => undefined);
    const isNewAccount = (lifetime?.orders ?? -1) === 0 && (lifetime?.tickets ?? -1) === 0 && (lifetime?.quotes ?? -1) === 0;

    if (isNewAccount) {
        const sess = await sessionRes.promise.catch(() => undefined);
        document.querySelector('#ov-welcome')?.classList.remove('d-none');
        if (sess?.user.firstName) {
            (document.querySelector('#ov-welcome-title') as HTMLElement).textContent = i18n('accountWelcomeName').replace('%s', sess.user.firstName);
        }
        for (const id of ['#ov-established', '#ov-inflight', '#ov-shipments', '#ov-tickets', '#ov-molds', '#ov-spend', '#ov-rep']) {
            document.querySelector(id)?.classList.add('d-none');
        }
        document.querySelector('.dashboard-grid-bar')?.classList.add('d-none');
    }

    wireSearch(inflightRes);

    const regions: region[] = [];
    let feed: MultiLoader | undefined;
    let feedMarked = false;

    function add(r: region) {
        const $err = r.$sec.querySelector('.ov-region-error') as HTMLElement | null;
        if ($err) {
            $err.replaceChildren(template.Clone('#ov-region-error-template'));
            $err.querySelector('.ov-retry')?.addEventListener('click', () => {
                const failed = r.resources.filter(res => res.failed);
                const reload = failed.length > 0 ? failed : r.resources;
                for (const res of reload) {
                    void res.reload();
                }
                for (const other of regions) {
                    if (other.resources.some(dep => reload.includes(dep))) {
                        void runRegion(other);
                    }
                }
            });
        }
        regions.push(r);
        void runRegion(r);
    }

    async function runRegion(r: region) {
        setRegionState(r.$sec, 'loading');
        r.$sec.classList.remove('d-none');
        const settled = await Promise.allSettled(r.resources.map(res => res.promise));
        if (settled.some(s => s.status === 'rejected')) {
            setRegionState(r.$sec, 'error');
            return;
        }
        try {
            const has = await r.render();
            if (r.hideWhenEmpty && !has) {
                r.$sec.classList.add('d-none');
                return;
            }
            r.$sec.classList.remove('d-none');
            setRegionState(r.$sec, 'ready');
        } catch {
            setRegionState(r.$sec, 'error');
        }
    }

    add({
        $sec: document.querySelector('#ov-identity') as HTMLElement,
        resources: [sessionRes, defaultsRes],
        render: async () => {
            await drawIdentity(await sessionRes.promise, await defaultsRes.promise);
            return true;
        },
    });

    add({
        $sec: document.querySelector('#ov-activity') as HTMLElement,
        resources: [sessionRes],
        render: async () => {
            await sessionRes.promise;
            const $feed = document.querySelector('#ov-feed') as HTMLElement;
            const $empty = document.querySelector('#ov-feed-empty') as HTMLElement;
            const $moreWrap = document.querySelector('#ov-feed-more-wrap') as HTMLElement;
            const $more = document.querySelector('#ov-feed-more') as HTMLButtonElement;
            $feed.replaceChildren();
            $empty.classList.add('d-none');
            let feedExhausted = false;
            feed = new MultiLoader({
                $target: $feed,
                pageSize: 10,
                autoContinue: false,
                loaders: TimelineLoaders({
                    lastSeenISO,
                    proofs: () => proofsRes.promise,
                }),
                done: () => {
                    feedExhausted = true;
                    $moreWrap.classList.add('d-none');
                },
            });
            await feed.load(true);
            stampFeedDividers($feed);
            const hasRows = $feed.querySelector('.ov-feed-row') !== null;
            $empty.classList.toggle('d-none', hasRows);
            $moreWrap.classList.toggle('d-none', !hasRows || feedExhausted);
            $more.onclick = () => {
                void feed?.load().then(() => stampFeedDividers($feed));
            };
            if (hasRows && !feedMarked) {
                try {
                    localStorage.setItem(lastSeenKey, new Date().toISOString());
                } catch {
                    // ignore quota / private mode
                }
                feedMarked = true;
            }
            return true;
        },
    });

    if (isNewAccount) {
        return;
    }

    add({
        $sec: document.querySelector('#ov-inflight') as HTMLElement,
        resources: [inflightRes, deliveredYearRes, sessionRes],
        render: async () => {
            const inflight = await inflightRes.promise;
            const counts = new Map<string, number>(inflightKeys.map(key => [key, 0]));
            for (const order of inflight) {
                const key = OrderStateOf(order)?.key;
                if (key) {
                    counts.set(key, (counts.get(key) ?? 0) + 1);
                }
            }
            counts.set('delivered', await deliveredYearRes.promise);
            drawInflight(counts, inflight, (await sessionRes.promise).user);
            return true;
        },
    });
    add({
        $sec: document.querySelector('#ov-shipments') as HTMLElement,
        resources: [inflightRes],
        hideWhenEmpty: true,
        render: async () => drawShipments(await inflightRes.promise),
    });
    add({
        $sec: document.querySelector('#ov-tickets') as HTMLElement,
        resources: [openTicketsRes],
        hideWhenEmpty: true,
        render: async () => drawTickets(await openTicketsRes.promise),
    });
    add({
        $sec: document.querySelector('#ov-molds') as HTMLElement,
        resources: [proofsRes],
        hideWhenEmpty: true,
        render: async () => drawMolds(await proofsRes.promise),
    });
    add({
        $sec: document.querySelector('#ov-spend') as HTMLElement,
        resources: [spendRes],
        hideWhenEmpty: true,
        render: async () => drawSpend(await spendRes.promise),
    });
    add({
        $sec: document.querySelector('#ov-attention') as HTMLElement,
        resources: [inflightRes, openTicketsRes, proofsRes, recentQuotesRes],
        hideWhenEmpty: true,
        render: async () => drawAttention(
            await inflightRes.promise,
            await openTicketsRes.promise,
            await proofsRes.promise,
            await recentQuotesRes.promise,
        ),
    });

    void Promise.all([
        proofsRes.promise.catch(() => [] as models.HyejooProof[]),
        inflightRes.promise.catch(() => [] as models.HyejooOrder[]),
    ]).then(([proofs, orders]) => {
        (document.querySelector('#ov-tile-reorder') as HTMLAnchorElement).href =
            groupMolds(proofs).length > 0 ? '#ov-molds' : Relref('/account/orders');
        (document.querySelector('#ov-tile-track') as HTMLAnchorElement).href =
            transitShipments(orders).length > 0 ? '#ov-shipments' : `${Relref('/account/orders')}?status=on-the-way`;
    });

    void salesRepRes.promise.then(rep => {
        if (rep) {
            drawRep(rep);
        }
    }, () => undefined);
}

function resource<T>(load: (b: Batch) => Promise<T>, initial?: Batch): resource<T> {
    const r: resource<T> = {
        promise: undefined as unknown as Promise<T>,
        failed: false,
        reload: () => {
            const b = new Batch();
            attach(load(b));
            void b.exec();
            return r.promise;
        },
    };
    const attach = (p: Promise<T>) => {
        r.failed = false;
        r.promise = p.then(value => {
            r.failed = false;
            return value;
        }, err => {
            r.failed = true;
            throw err;
        });
    };
    if (initial) {
        attach(load(initial));
    } else {
        const b = new Batch();
        attach(load(b));
        void b.exec();
    }
    return r;
}

async function drain<T>(b: Batch, pageFn: (batch: Batch, p: number) => Promise<{ rows: T[]; nextPage: boolean }>): Promise<T[]> {
    const first = await pageFn(b, 0);
    const rows = [...first.rows];
    let p = 1;
    let more = first.nextPage;
    while (more) {
        const nb = new Batch();
        const next = pageFn(nb, p);
        void nb.exec();
        const data = await next;
        rows.push(...data.rows);
        more = data.nextPage;
        p++;
    }
    return rows;
}

function loadInflight(b: Batch): Promise<models.HyejooOrder[]> {
    return drain(b, (batch, p) => Orders({
        q: InflightQ,
        data: inflightData,
        sort: 'created desc',
        max: 100,
        p,
    }).then(data => ({ rows: data.results, nextPage: data.nextPage }))).then(orders => {
        orders.sort((a, b) => {
            const aShip = a.customerShipDate.isValid;
            const bShip = b.customerShipDate.isValid;
            if (aShip && bShip) {
                const diff = a.customerShipDate.toMillis() - b.customerShipDate.toMillis();
                if (diff !== 0) {
                    return diff;
                }
            } else if (aShip) {
                return -1;
            } else if (bShip) {
                return 1;
            }
            return b.created.toMillis() - a.created.toMillis();
        });
        return orders;
    });
}

function setRegionState($sec: HTMLElement, state: 'loading' | 'error' | 'ready') {
    $sec.querySelector('.ov-region-loading')?.classList.toggle('d-none', state !== 'loading');
    $sec.querySelector('.ov-region-error')?.classList.toggle('d-none', state !== 'error');
    $sec.querySelector('.ov-region-body')?.classList.toggle('d-none', state !== 'ready');
    $sec.querySelector('.ov-sec-link')?.classList.toggle('d-none', state !== 'ready');
}

function sizeOf(lineItem: { lineItemTypeSize?: models.LineItemTypeSize; size?: string }): string {
    return lineItem.lineItemTypeSize?.name || lineItem.size || '';
}

function productDescription(order: models.HyejooOrder): string {
    const lines = order.orderLineItems.filter(lineItem => lineItem.lineItemModel?.product === true);
    const first = lines[0];
    if (!first) {
        return '';
    }
    const plural = first.lineItemModel?.pluralName || first.lineItemModel?.name || '';
    const type = first.lineItemType?.name || '';
    // A line item can be missing its type or size (older internal orders), so
    // the separators have to drop with them — never "3 × Medals — , 2.75".
    let desc = [`${first.quantity} × ${plural}`, [type, sizeOf(first)].filter(Boolean).join(', ')].filter(Boolean).join(' — ');
    if (lines.length > 1) {
        desc += i18n('accountPlusMore').replace('%s', String(lines.length - 1));
    }
    return desc;
}

// A quote with no product line has no description, and the heading's separator
// would dangle after the serial.
function quoteHeading(quote: models.Quote): string {
    const desc = quoteDescription(quote);
    const heading = i18n('accountQuoteHeading').replace('{serial}', String(quote.serial)).replace('{desc}', desc);
    return desc ? heading : heading.replace(/\s*·\s*$/, '');
}

function quoteDescription(quote: models.Quote): string {
    const lines = quote.quoteLineItems.filter(lineItem => lineItem.lineItemModel?.product === true);
    const first = lines[0] ?? quote.quoteLineItems[0];
    if (!first) {
        return '';
    }
    const plural = first.lineItemModel?.pluralName || first.lineItemModel?.name || first.lineItem || '';
    const type = first.lineItemType?.name || '';
    const size = sizeOf(first);
    return [plural, type, size].filter(Boolean).join(', ');
}

function poChip(order: models.HyejooOrder): string {
    for (const payment of order.orderPayments) {
        if (payment.purchaseOrderNumber) {
            return payment.purchaseOrderNumber;
        }
    }
    for (const payment of order.orderPayments) {
        const match = payment.paymentOption?.address.fullName ? poFromName.exec(payment.paymentOption.address.fullName) : null;
        if (match) {
            return match[0];
        }
    }
    return '';
}

function clientLine(order: models.HyejooOrder, user: models.User): string {
    const option = order.orderShippingOptions[0]?.shippingOption;
    if (!option) {
        return '';
    }
    const label = (option.address?.organization || option.address?.fullName || '').trim();
    if (!label) {
        return '';
    }
    const company = user.company.trim().toLowerCase();
    const full = `${user.firstName} ${user.lastName}`.trim().toLowerCase();
    const lower = label.toLowerCase();
    if (company && lower === company) {
        return '';
    }
    if (full && lower === full) {
        return '';
    }
    return label;
}

function cardDeclined(order: models.HyejooOrder): boolean {
    return order.orderPayments.some(payment => payment.paymentOption?.declined === true);
}

function transitShipments(orders: models.HyejooOrder[]): transitShipment[] {
    const out: transitShipment[] = [];
    for (const order of orders) {
        if (OrderStateOf(order)?.key !== 'on-the-way') {
            continue;
        }
        const group: transitShipment[] = [];
        for (const row of CustomerShipmentRows(order)) {
            if (row.shipment.deliveredAt.isValid) {
                continue;
            }
            group.push({ order, shippingOption: row.shippingOption, shipment: row.shipment, index: 0, total: 0 });
        }
        for (let i = 0; i < group.length; i++) {
            group[i].index = i + 1;
            group[i].total = group.length;
            out.push(group[i]);
        }
    }
    return out;
}

function groupMolds(proofs: models.HyejooProof[]): moldCard[] {
    const groups = new Map<string, models.HyejooProof[]>();
    for (const proof of proofs) {
        const key = MoldIdentity(proof);
        const list = groups.get(key) ?? [];
        list.push(proof);
        groups.set(key, list);
    }
    const today = DateTime.now().startOf('day');
    const cards: moldCard[] = [];
    for (const [key, list] of groups) {
        let proof = list[0];
        let newest = 0;
        const ordersByID = new Map<string, models.Order>();
        for (const item of list) {
            // One orderproof per side, so the same order can appear twice.
            for (const orderProof of item.orderProofs) {
                const order = orderProof.order;
                if (!order) {
                    continue;
                }
                if (order.id && !ordersByID.has(order.id)) {
                    ordersByID.set(order.id, order);
                }
                if (order.confirmedTime.isValid && order.confirmedTime.toMillis() >= newest) {
                    newest = order.confirmedTime.toMillis();
                    proof = item;
                }
            }
        }
        const sides = moldSides(proof, today);
        const soon = sides.some(side => side.status === 'expires' && side.date.startOf('day').diff(today, 'days').days <= 30);
        cards.push({ key, proof, orders: [...ordersByID.values()], sides, soon });
    }
    cards.sort((a, b) => {
        const aAlive = a.sides.some(side => side.status === 'expires');
        const bAlive = b.sides.some(side => side.status === 'expires');
        if (aAlive && bAlive) {
            const aSoon = Math.min(...a.sides.filter(side => side.status === 'expires').map(side => side.date.toMillis()));
            const bSoon = Math.min(...b.sides.filter(side => side.status === 'expires').map(side => side.date.toMillis()));
            return aSoon - bSoon;
        }
        if (aAlive) {
            return -1;
        }
        if (bAlive) {
            return 1;
        }
        const aLapsed = a.sides.length > 0 && a.sides.every(side => side.status === 'lapsed');
        const bLapsed = b.sides.length > 0 && b.sides.every(side => side.status === 'lapsed');
        if (aLapsed && bLapsed) {
            const aNew = Math.max(...a.sides.map(side => side.date.toMillis()));
            const bNew = Math.max(...b.sides.map(side => side.date.toMillis()));
            return bNew - aNew;
        }
        if (aLapsed) {
            return -1;
        }
        if (bLapsed) {
            return 1;
        }
        return 0;
    });
    return cards;
}

function moldSides(proof: models.HyejooProof, today: DateTime): moldSide[] {
    const front = proof.frontMoldExpires;
    const back = proof.backMoldExpires;
    const two = front.isValid && back.isValid && !front.hasSame(back, 'day');
    if (two) {
        return [sideOf('front', front, today), sideOf('back', back, today)];
    }
    let one = back;
    if (proof.moldExpires.isValid) {
        one = proof.moldExpires;
    } else if (front.isValid) {
        one = front;
    }
    if (!one.isValid) {
        return [{ side: '', status: 'none', date: DateTime.invalid('none') }];
    }
    return [sideOf('', one, today)];
}

function sideOf(side: 'front' | 'back' | '', date: DateTime, today: DateTime): moldSide {
    return {
        side,
        status: date.startOf('day') < today ? 'lapsed' : 'expires',
        date,
    };
}

function moldLabel(proof: models.HyejooProof): { label: string; secondary: string } {
    const label = MoldLabel(proof) || i18n('accountSearchKindMold');
    // The product line is only worth a second line when the label is a name.
    const lineItem = proof.latestOrderLineItem;
    const secondary = proof.projectName && lineItem ? `${lineItem.lineItemModel?.pluralName || ''} · ${sizeOf(lineItem)}` : '';
    return { label, secondary };
}

function expLabel(raw: string): string {
    if (!raw) {
        return '';
    }
    const iso = DateTime.fromISO(raw);
    if (iso.isValid) {
        // A card expiry is the card's printed MM/YY, not a locale date.
        return iso.toFormat('MM/yy');
    }
    const match = /(\d{1,2})\D+(\d{2,4})/.exec(raw);
    if (!match) {
        return raw;
    }
    const mm = match[1].padStart(2, '0');
    const yy = match[2].length === 4 ? match[2].slice(2) : match[2];
    return `${mm}/${yy}`;
}

function paymentLabel(option: models.HyejooPaymentOption): string {
    if (option.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
        const kind = [option.bankType, option.accountType].filter(Boolean).map(CapitalizeFirstLetter).join(' ');
        if (!kind) {
            return i18n('accountBankAccount');
        }
        if (option.creditCardLast4) {
            return i18n('accountKindAccountLast4').replace('{kind}', kind).replace('{last4}', option.creditCardLast4);
        }
        return i18n('accountKindAccount').replace('%s', kind);
    }
    const brand = CapitalizeFirstLetter(getCreditCardType(option.creditCardFirst6.toString()).toString());
    const last4 = option.creditCardLast4.toString().padStart(4, '0');
    const card = `${brand} ···· ${last4}`;
    const exp = expLabel(option.creditCardExpirationDate);
    if (exp) {
        return i18n('accountCardExp').replace('{card}', card).replace('{exp}', exp);
    }
    return card;
}

function normalizeQuery(raw: string): string {
    let v = raw.trim();
    v = v.replace(/^#/, '');
    v = v.replace(/^PO#?\s*/i, '');
    return v.toLowerCase();
}

function wireSearch(inflightRes: resource<models.HyejooOrder[]>) {
    const $form = document.querySelector('#ov-search-form') as HTMLFormElement;
    const $input = document.querySelector('#ov-search-input') as HTMLInputElement;
    const $result = document.querySelector('#ov-search-result') as HTMLElement;
    $input.addEventListener('input', () => {
        $result.classList.add('d-none');
        $result.replaceChildren();
    });
    $form.addEventListener('submit', e => {
        e.preventDefault();
        const typed = $input.value.trim();
        if (!typed) {
            $result.classList.add('d-none');
            $result.replaceChildren();
            return;
        }
        void runSearch(typed, inflightRes, $result);
    });
}

async function runSearch(typed: string, inflightRes: resource<models.HyejooOrder[]>, $result: HTMLElement) {
    const norm = normalizeQuery(typed);
    const inflight = await inflightRes.promise.catch(() => [] as models.HyejooOrder[]);
    const known: { k: string; label: string; sub: string; href: string }[] = [];
    for (const order of inflight) {
        known.push({
            k: String(order.invoiceNumber),
            label: i18n('accountOrderNumber').replace('%s', String(order.invoiceNumber)),
            sub: productDescription(order),
            href: `${Relref('/account/order')}?id=${order.id}`,
        });
        const po = poChip(order);
        if (po) {
            known.push({
                k: normalizeQuery(po),
                label: i18n('accountOrderNumber').replace('%s', String(order.invoiceNumber)),
                sub: po,
                href: `${Relref('/account/order')}?id=${order.id}`,
            });
        }
    }
    for (const row of transitShipments(inflight)) {
        if (row.shipment.trackingNumber) {
            const carrier = row.shipment.carrier?.name || '';
            known.push({
                k: row.shipment.trackingNumber.toLowerCase(),
                label: i18n('accountOrderNumber').replace('%s', String(row.order.invoiceNumber)),
                sub: [carrier, row.shipment.trackingNumber].filter(Boolean).join(' · '),
                href: `${Relref('/account/order')}?id=${row.order.id}`,
            });
        }
    }
    const local = known.find(item => item.k.toLowerCase() === norm);
    const onPage = pageHits(typed);
    if (local) {
        renderSearchHit($result, local);
        // An order number matches `known` before the page scan ever runs, and
        // the In flight tile folds all but the newest few away — so without
        // this the customer is told the order is theirs while its card stays
        // hidden.
        if (onPage.length > 0) {
            jumpToCard(onPage[0].$card);
        }
        return;
    }
    if (onPage.length > 0) {
        renderSearchHits($result, onPage);
        jumpToCard(onPage[0].$card);
        return;
    }
    // The server's /orders full-text search covers invoice numbers, totals,
    // customer/order names, proof names, PO numbers (and payment-option
    // names), and tracking numbers — one lookup instead of per-shape
    // heuristics. Colons neutralized so typed text can't parse as a field
    // filter (this hyejoo has no standalone quoted-term syntax).
    const data = await Orders({
        q: SearchTerm(typed),
        data: 'id,invoiceNumber,orderLineItems',
        max: 2,
    }).catch(() => undefined);
    if (data?.results.length === 1 && !data.nextPage) {
        const order = data.results[0];
        renderSearchHit($result, {
            label: i18n('accountOrderNumber').replace('%s', String(order.invoiceNumber)),
            sub: productDescription(order),
            href: `${Relref('/account/order')}?id=${order.id}`,
        });
        return;
    }
    renderSearchMiss($result, typed);
}

interface pageHit {
    label: string;
    sub: string;
    $card: HTMLElement;
}

// Matches against what the page already shows — every rendered card's text
// (mold labels, design names, order descriptions, clients, PO numbers,
// tracking numbers, feed rows) — case-insensitively.
function pageHits(typed: string): pageHit[] {
    const needle = typed.trim().toLowerCase();
    const hits: pageHit[] = [];
    for (const $card of document.querySelectorAll<HTMLElement>('[data-search]')) {
        const text = ($card.textContent ?? '').replace(/\s+/g, ' ').toLowerCase();
        if (!text.includes(needle)) {
            continue;
        }
        const detail = ($card.querySelector('[data-search-sub]')?.textContent ?? '').trim();
        hits.push({
            label: $card.dataset.searchLabel || ($card.querySelector('b')?.textContent ?? '').trim(),
            sub: [$card.dataset.search, detail].filter(Boolean).join(' · '),
            $card,
        });
    }
    return hits;
}

const searchHitsShown = 8;

function renderSearchHits($result: HTMLElement, hits: pageHit[]) {
    const $hits: HTMLElement[] = [];
    for (const hit of hits.slice(0, searchHitsShown)) {
        const $hit = template.Clone('#ov-search-hit-template').firstElementChild as HTMLAnchorElement;
        $hit.href = '#';
        ($hit.querySelector('b') as HTMLElement).textContent = hit.label;
        ($hit.querySelector('small') as HTMLElement).textContent = hit.sub;
        $hit.addEventListener('click', e => {
            e.preventDefault();
            jumpToCard(hit.$card);
        });
        $hits.push($hit);
    }
    if (hits.length > searchHitsShown) {
        const $more = document.createElement('p');
        $more.className = 'text-muted small mb-0 mt-2';
        $more.textContent = i18n('accountSearchMoreOnPage').replace('%s', String(hits.length - searchHitsShown));
        $hits.push($more);
    }
    $result.replaceChildren(...$hits);
    $result.classList.remove('d-none');
}

// Scroll the matched card into view and pulse it (.ov-hit; the animation
// removes itself so the next search can pulse the same card again).
function jumpToCard($card: HTMLElement) {
    if ($card.classList.contains('ov-mold-hidden')) {
        showAllMolds();
    }
    $card.classList.remove('d-none', 'ov-order-hidden');
    $card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    $card.classList.remove('ov-hit');
    $card.getBoundingClientRect();
    $card.classList.add('ov-hit');
    $card.addEventListener('animationend', () => $card.classList.remove('ov-hit'), { once: true });
}

function renderSearchHit($result: HTMLElement, hit: { label: string; sub: string; href: string }) {
    const $hit = template.Clone('#ov-search-hit-template').firstElementChild as HTMLAnchorElement;
    $hit.href = hit.href;
    ($hit.querySelector('b') as HTMLElement).textContent = hit.label;
    ($hit.querySelector('small') as HTMLElement).textContent = hit.sub;
    $result.replaceChildren($hit);
    $result.classList.remove('d-none');
}

function renderSearchMiss($result: HTMLElement, q: string) {
    const $miss = template.Clone('#ov-search-miss-template').firstElementChild as HTMLElement;
    ($miss.querySelector('.ov-search-miss') as HTMLElement).textContent = i18n('accountSearchMiss').replace('%s', q);
    ($miss.querySelector('.ov-search-orders') as HTMLAnchorElement).href = `${Relref('/account/orders')}?q=${encodeURIComponent(q)}`;
    ($miss.querySelector('.ov-search-orders-label') as HTMLElement).textContent = i18n('accountSearchOrdersFor').replace('%s', q);
    ($miss.querySelector('.ov-search-files') as HTMLAnchorElement).href = `${Relref('/account/files')}?q=${encodeURIComponent(q)}`;
    $result.replaceChildren($miss);
    $result.classList.remove('d-none');
}

async function drawIdentity(sess: models.SessionGetResponse, d: defaults) {
    const $body = document.querySelector('#ov-identity-body') as HTMLElement;
    const $el = template.Clone('#ov-identity-template').firstElementChild as HTMLElement;
    const user = sess.user;
    ($el.querySelector('.avatar') as HTMLElement).textContent = initials(user.firstName, user.company);
    ($el.querySelector('.ov-id-name') as HTMLElement).textContent = user.firstName;
    if (user.company) {
        const $co = $el.querySelector('.ov-id-co') as HTMLElement;
        $co.textContent = user.company;
        $co.classList.remove('d-none');
    }
    const year = user.created.isValid ? String(user.created.year) : '';
    ($el.querySelector('.ov-id-since') as HTMLElement).textContent = year ? i18n('accountCustomerSince').replace('%s', year) : '';
    if (user.balance.gt(0)) {
        const $credit = $el.querySelector('.ov-id-credit') as HTMLElement;
        $credit.classList.remove('d-none');
        $credit.classList.add('d-flex');
        ($el.querySelector('.ov-id-credit-amt') as HTMLElement).textContent = i18n('accountStoreCredit').replace('%s', DollarFormat(user.balance));
    }
    ($el.querySelector('.ov-id-pay') as HTMLElement).textContent = d.payment ? paymentLabel(d.payment) : i18n('accountNoneYetAdd');
    const $ship = $el.querySelector('.ov-id-ship') as HTMLElement;
    if (d.shipping) {
        $ship.replaceChildren(...addressLines(d.shipping));
    } else {
        $ship.textContent = i18n('accountNoneOnFile');
    }
    ($el.querySelector('.ov-id-tax') as HTMLElement).textContent = await taxLabel(d.tax);
    $body.replaceChildren($el);
}

function addressLines(option: models.HyejooShippingOption): HTMLElement[] {
    const address = option.address;
    const lines: string[] = [];
    if (address.fullName) {
        lines.push(address.fullName);
    }
    if (address.organization) {
        lines.push(address.organization);
    }
    const street = [address.addressLine1, address.addressLine2].filter(Boolean).join(', ');
    if (street) {
        lines.push(street);
    }
    if (address.city && address.state?.stateCode) {
        lines.push(`${address.city}, ${address.state.stateCode} ${address.postalCode}`.trim());
    } else {
        const cityLine = [address.city, address.state?.stateCode, address.postalCode].filter(Boolean).join(' ');
        if (cityLine) {
            lines.push(cityLine);
        }
    }
    return lines.map(text => {
        const $span = document.createElement('span');
        $span.className = 'd-block';
        $span.textContent = text;
        return $span;
    });
}

async function taxLabel(rows: models.HyejooUserTaxExemption[]): Promise<string> {
    if (rows.length === 0) {
        return i18n('accountNothingOnFile');
    }
    if (rows.length > 1) {
        return i18n('accountCertificatesOnFile').replace('%s', String(rows.length));
    }
    const ute = rows[0];
    const reason = ute.userTaxExemptionReason?.name || '';
    const state = await countries.GetState(ute.countryID, ute.stateID);
    const stateName = state?.name || state?.stateCode || '';
    const number = ute.number ? `#${ute.number}` : '';
    return [reason, stateName, number].filter(Boolean).join(' · ');
}

function initials(firstName: string, company: string): string {
    const a = firstName[0] || '';
    const b = company ? company[0] : firstName[1] || '';
    return `${a}${b}`.toUpperCase();
}

function drawRep(rep: models.SessionSalesRep) {
    const $host = document.querySelector('#ov-rep') as HTMLElement;
    const $el = template.Clone('#ov-rep-template').firstElementChild as HTMLElement;
    const name = `${rep.firstName} ${rep.lastName}`.trim();
    ($el.querySelector('.avatar') as HTMLElement).textContent = name.split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase();
    ($el.querySelector('.ov-rep-name') as HTMLElement).textContent = name;
    const $email = $el.querySelector('.ov-rep-email') as HTMLAnchorElement;
    $email.textContent = rep.email;
    $email.href = `mailto:${rep.email}`;
    ($el.querySelector('.ov-rep-btn') as HTMLAnchorElement).href = `mailto:${rep.email}`;
    $host.replaceChildren($el);
    $host.classList.remove('d-none');
}

// An empty frame reads as a broken image, so a row with nothing behind it drops
// its picture slot rather than keeping the gap where one would have been.
function fillArt($slot: Element | null, $art: HTMLElement | undefined) {
    if (!$slot) {
        return;
    }
    if ($art) {
        $slot.replaceWith($art);
    } else {
        $slot.remove();
    }
}

// A distributor can have thirty orders live at once; the tile shows the newest
// few and "All Orders" in the section head browses the rest.
const inflightShown = 5;

function drawInflight(counts: Map<string, number>, orders: models.HyejooOrder[], user: models.User) {
    const $counts = document.querySelector('#ov-counts') as HTMLElement;
    $counts.replaceChildren();
    for (const key of countChipOrder) {
        const state = OrderStateByKey(key);
        if (!state) {
            continue;
        }
        const n = counts.get(key) ?? 0;
        const $chip = template.Clone('#ov-count-template').firstElementChild as HTMLAnchorElement;
        $chip.href = `${Relref('/account/orders')}?status=${state.key}`;
        const $n = $chip.querySelector('b') as HTMLElement;
        $n.textContent = String(n);
        $chip.classList.add(`tone-${key}`);
        ($chip.querySelector('span') as HTMLElement).textContent = key === 'delivered' ? i18n('orderStateDeliveredThisYear') : state.label;
        if (n === 0) {
            $chip.classList.add('is-zero');
        }
        $counts.append($chip);
    }
    const $list = document.querySelector('#ov-inflight-list') as HTMLElement;
    $list.replaceChildren();
    const $empty = document.querySelector('#ov-inflight-empty') as HTMLElement;
    const $count = document.querySelector('#ov-inflight-count') as HTMLElement;
    $count.textContent = String(orders.length);
    $count.classList.remove('d-none');
    if (orders.length === 0) {
        $empty.classList.remove('d-none');
        return;
    }
    $empty.classList.add('d-none');
    // The tail stays in the DOM so the page search can still find it and
    // reveal it on a hit.
    for (const [i, order] of orders.entries()) {
        const $card = drawOrderCard(order, user);
        if (i >= inflightShown) {
            $card.classList.add('d-none', 'ov-order-hidden');
        }
        $list.append($card);
    }
}

function drawOrderCard(order: models.HyejooOrder, user: models.User): HTMLElement {
    const $card = template.Clone('#ov-order-template').firstElementChild as HTMLElement;
    const $num = $card.querySelector('.ov-order-num') as HTMLAnchorElement;
    $num.href = `${Relref('/account/order')}?id=${order.id}`;
    $num.textContent = `#${order.invoiceNumber}`;
    fillArt($card.querySelector('.ov-order-art'), ProofStack(OrderArt(order), { size: 'md', enlarge: true }));
    const po = poChip(order);
    if (po) {
        const $po = $card.querySelector('.ov-order-po') as HTMLElement;
        $po.textContent = i18n('accountPONumber').replace('%s', po.replace(/^PO#?\s*/i, ''));
        $po.classList.remove('d-none');
    }
    const state = OrderStateOf(order);
    $card.dataset.searchLabel = i18n('accountOrderNumber').replace('%s', String(order.invoiceNumber));
    const $tag = $card.querySelector('.ov-tag') as HTMLElement;
    $tag.textContent = state?.label ?? i18n('orderStateUnknown');
    $tag.classList.add(state?.tag ?? 'tag-warning');
    ($card.querySelector('.ov-order-desc') as HTMLElement).textContent = productDescription(order);
    const client = clientLine(order, user);
    if (client) {
        const $client = $card.querySelector('.ov-order-client') as HTMLElement;
        $client.classList.remove('d-none');
        $client.classList.add('d-flex');
        ($card.querySelector('.ov-order-client-name') as HTMLElement).textContent = client;
    }
    const awaiting = state?.key === 'awaiting-payment';
    const $ships = $card.querySelector('.ov-order-ships') as HTMLElement;
    if (awaiting) {
        $ships.classList.remove('d-none');
        $ships.classList.add('ov-date-risk');
        ($ships.querySelector('b') as HTMLElement).textContent = i18n('accountWaitingOnPayment');
    } else if (order.customerShipDate.isValid) {
        $ships.classList.remove('d-none');
        ($ships.querySelector('b') as HTMLElement).textContent = i18n('timelineShipsBy').replace('%s', order.customerShipDate.toLocaleString(DateTime.DATE_MED));
    }
    if (order.eventDate.isValid) {
        const $needed = $card.querySelector('.ov-order-needed') as HTMLElement;
        $needed.classList.remove('d-none');
        $needed.textContent = i18n('accountNeededBy').replace('%s', order.eventDate.toLocaleString(DateTime.DATE_MED));
    }
    const atRisk = order.customerShipDate.isValid && order.eventDate.isValid && order.customerShipDate > order.eventDate;
    if (atRisk) {
        $ships.classList.add('ov-date-risk');
        const $risk = $card.querySelector('.ov-risk') as HTMLElement;
        $risk.classList.remove('d-none');
        $risk.classList.add('d-flex');
    }
    // The reason belongs on the card, not only in the attention list.
    if (awaiting && cardDeclined(order)) {
        const $declined = $card.querySelector('.ov-declined') as HTMLElement;
        ($declined.querySelector('.ov-declined-text') as HTMLElement).textContent =
            i18n('accountCardDeclined').replace('%s', DollarFormat(order.balance));
        $declined.classList.remove('d-none');
        $declined.classList.add('d-flex');
    }
    const $total = $card.querySelector('.ov-order-total') as HTMLElement;
    // A balance on a PO-paid order is normal — red "due" is only for the
    // awaiting-payment state.
    if (state?.key === 'awaiting-payment' && order.balance.gt(0.05)) {
        const $due = document.createElement('b');
        $due.className = 'text-danger';
        $due.textContent = i18n('accountDue').replace('%s', DollarFormat(order.balance));
        $total.replaceChildren($due);
    } else {
        $total.textContent = DollarFormat(order.total);
    }
    return $card;
}

async function drawShipments(orders: models.HyejooOrder[]): Promise<boolean> {
    const rows = transitShipments(orders);
    const $list = document.querySelector('#ov-shipments-list') as HTMLElement;
    const $count = document.querySelector('#ov-shipments-count') as HTMLElement;
    $list.replaceChildren();
    if (rows.length === 0) {
        return false;
    }
    $count.textContent = String(rows.length);
    $count.classList.remove('d-none');
    const carriers = await Carriers({ data: 'name,link' }).catch(() => new Map<NonNullable<models.Carrier['id']>, models.Carrier>());
    for (const row of rows) {
        $list.append(drawShipmentCard(row, carriers));
    }
    void fillTracking(rows);
    return true;
}

function drawShipmentCard(row: transitShipment, carriers: Map<NonNullable<models.Carrier['id']>, models.Carrier>): HTMLElement {
    const $card = template.Clone('#ov-ship-template').firstElementChild as HTMLElement;
    $card.dataset.shipmentId = row.shipment.id;
    $card.dataset.searchLabel = `${i18n('accountOrderNumber').replace('%s', String(row.order.invoiceNumber))} · ${row.shipment.trackingNumber}`;
    $card.dataset.orderId = row.order.id;
    const lines = row.order.orderLineItems.filter(lineItem => lineItem.lineItemModel?.product === true);
    const first = lines[0];
    const plural = first?.lineItemModel?.pluralName || first?.lineItemModel?.name || '';
    let desc = first ? `${first.quantity} × ${plural}` : i18n('accountOrderNumber').replace('%s', String(row.order.invoiceNumber));
    if (row.total > 1) {
        desc = i18n('accountShipBox').
            replace('{desc}', desc).
            replace('{index}', String(row.index)).
            replace('{total}', String(row.total));
    }
    ($card.querySelector('.ov-ship-desc') as HTMLElement).textContent = desc;
    // A box has no artwork of its own — it stands for the order it carries, so
    // every box of one order shows the same pictures.
    fillArt($card.querySelector('.ov-ship-art'), ProofStack(OrderArt(row.order), { size: 'md', enlarge: true }));
    const $order = $card.querySelector('.ov-ship-order') as HTMLAnchorElement;
    $order.textContent = `#${row.order.invoiceNumber}`;
    $order.href = `${Relref('/account/order')}?id=${row.order.id}`;
    const carrier = carriers.get(row.shipment.carrierID);
    ($card.querySelector('.ov-ship-carrier') as HTMLElement).textContent = carrier?.name || row.shipment.carrier?.name || '';
    const $num = $card.querySelector('.ov-ship-num') as HTMLAnchorElement;
    $num.textContent = row.shipment.trackingNumber;
    $num.href = (carrier?.link ?? '').replace('%%', row.shipment.trackingNumber) || '#';
    const $copy = $card.querySelector('.ov-copy') as HTMLButtonElement;
    $copy.addEventListener('click', async e => {
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(row.shipment.trackingNumber);
        } catch {
            // Clipboard denied — nothing was copied, so don't claim it was.
            return;
        }
        $copy.classList.add('is-copied');
        ($copy.querySelector('.ov-copy-icon') as HTMLElement).textContent = 'check';
        ($copy.querySelector('.ov-copy-label') as HTMLElement).textContent = i18n('accountCopied');
        window.setTimeout(() => {
            $copy.classList.remove('is-copied');
            ($copy.querySelector('.ov-copy-icon') as HTMLElement).textContent = 'content_copy';
            ($copy.querySelector('.ov-copy-label') as HTMLElement).textContent = i18n('accountCopy');
        }, 1800);
    });
    const shippingOption = row.shippingOption.shippingOption;
    const shippingAddress = shippingOption?.address;
    const dest = [shippingAddress?.city, shippingAddress?.state?.stateCode].filter(Boolean).join(', ');
    ($card.querySelector('.ov-ship-dest-text') as HTMLElement).textContent = dest ? i18n('accountToDest').replace('%s', dest) : '';
    const shipped = row.shipment.shippedAt.isValid ? row.shipment.shippedAt : row.shipment.created;
    ($card.querySelector('.ov-ship-shipped') as HTMLElement).textContent = shipped.isValid ? i18n('accountShipped').replace('%s', shipped.toLocaleString(DateTime.DATE_MED)) : '';
    ($card.querySelector('.ov-ship-eta') as HTMLElement).textContent = i18n('accountDeliveryEstimatePending');
    if (row.shipment.exceptionAt.isValid) {
        const $status = $card.querySelector('.ov-ship-status') as HTMLElement;
        ($card.querySelector('.ov-ship-status-icon') as HTMLElement).textContent = 'error';
        $status.classList.add('is-exception');
    }
    return $card;
}

async function fillTracking(rows: transitShipment[]) {
    const uniqueShipmentIDs: string[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
        if (!row.shipment.id || seen.has(row.shipment.id)) {
            continue;
        }
        seen.add(row.shipment.id);
        uniqueShipmentIDs.push(row.shipment.id);
    }
    if (uniqueShipmentIDs.length === 0) {
        return;
    }
    try {
        // Unlimited because the id list bounds the read.
        const resp = await Shipments({
            q: `id:${uniqueShipmentIDs.join(',')}`,
            data: 'id,tracking',
            max: -1,
        });
        for (const result of resp.results) {
            const $card = document.querySelector(`[data-shipment-id="${result.id}"]`) as HTMLElement | null;
            if (!$card) {
                continue;
            }
            const $eta = $card.querySelector('.ov-ship-eta') as HTMLElement;
            if (result.tracking.estimatedDelivery.isValid) {
                const $b = document.createElement('b');
                $b.textContent = i18n('accountEstDelivery').replace('%s', result.tracking.estimatedDelivery.toLocaleString(DateTime.DATE_MED));
                $eta.replaceChildren($b);
            }
            if (result.tracking.statusDescription) {
                const loc = [result.tracking.city, result.tracking.state].filter(Boolean).join(', ');
                const line = loc ? `${result.tracking.statusDescription} — ${loc}` : result.tracking.statusDescription;
                const $status = $card.querySelector('.ov-ship-status') as HTMLElement;
                ($card.querySelector('.ov-ship-status-text') as HTMLElement).textContent = line;
                $status.classList.remove('d-none');
                $status.classList.add('d-flex');
            }
        }
    } catch {
        // leave estimate pending
    }
}

function drawTickets(tickets: models.HyejooQuoteRequestProjectTask[]): boolean {
    const $list = document.querySelector('#ov-tickets-list') as HTMLElement;
    const $count = document.querySelector('#ov-tickets-count') as HTMLElement;
    $list.replaceChildren();
    if (tickets.length === 0) {
        return false;
    }
    $count.textContent = String(tickets.length);
    $count.classList.remove('d-none');
    for (const task of tickets.slice(0, 5)) {
        $list.append(drawTicketCard(task));
    }
    return true;
}

function drawTicketCard(task: models.HyejooQuoteRequestProjectTask): HTMLElement {
    const $card = template.Clone('#ov-ticket-template').firstElementChild as HTMLAnchorElement;
    const name = task.quoteRequest?.quoteRequestProject?.name || i18n('timelineDesignTicket');
    $card.href = `${Relref('/design-ticket')}?qrpt=${task.key}`;
    ($card.querySelector('.ov-ticket-name') as HTMLElement).textContent = name;
    $card.dataset.searchLabel = name;
    const status = TicketStatus(task);
    const $status = $card.querySelector('.ov-ticket-status') as HTMLElement;
    $status.textContent = status.label;
    $status.classList.add(status.tag);
    ($card.querySelector('.ov-ticket-kind') as HTMLElement).textContent = TicketTypeLabel(task);
    if (task.assignedUser?.firstName) {
        const $d = $card.querySelector('.ov-ticket-designer') as HTMLElement;
        $d.textContent = i18n('accountWithDesigner').replace('%s', task.assignedUser.firstName);
        $d.classList.remove('d-none');
    }
    const expected = TicketExpectedBackLine(task);
    const need = TicketNeedByLine(task);
    ($card.querySelector('.ov-ticket-dates') as HTMLElement).textContent = [expected, need].filter(Boolean).join(' · ');
    // A ticket always keeps its tile: the artwork once there is any, the icon
    // fallback while the artist has not drawn one.
    const art = TicketArt(task, name);
    fillArt($card.querySelector('.ov-ticket-art'), ProofStack(art.length > 0 ? art : [{ src: '', alt: name }], { size: 'lg' }));
    return $card;
}

// A distributor can own a hundred molds; the region shows the ones that
// matter first (soonest to lapse) and unfolds the rest on demand.
const moldsShown = 8;

function showAllMolds() {
    for (const $card of document.querySelectorAll('#ov-molds-list .ov-mold-hidden')) {
        $card.classList.remove('d-none', 'ov-mold-hidden');
    }
    document.querySelector('#ov-molds-more-wrap')?.classList.add('d-none');
}

function drawMolds(proofs: models.HyejooProof[]): boolean {
    const cards = groupMolds(proofs);
    const $list = document.querySelector('#ov-molds-list') as HTMLElement;
    const $count = document.querySelector('#ov-molds-count') as HTMLElement;
    const $moreWrap = document.querySelector('#ov-molds-more-wrap') as HTMLElement;
    const $more = document.querySelector('#ov-molds-more') as HTMLButtonElement;
    $list.replaceChildren();
    $moreWrap.classList.add('d-none');
    if (cards.length === 0) {
        return false;
    }
    $count.textContent = String(cards.length);
    $count.classList.remove('d-none');
    const fees = new Map<string, Promise<Big | undefined>>();
    // Every mold is rendered so the page search can find it; the tail stays
    // hidden until "Show all" (or a search hit inside it) reveals it.
    for (const [i, card] of cards.entries()) {
        const $card = drawMoldCard(card, fees);
        if (i >= moldsShown) {
            $card.classList.add('d-none', 'ov-mold-hidden');
        }
        $list.append($card);
    }
    if (cards.length > moldsShown) {
        ($more.querySelector('.ov-molds-more-label') as HTMLElement).textContent = i18n('moldShowAll').replace('%s', String(cards.length));
        $moreWrap.classList.remove('d-none');
        $more.onclick = () => showAllMolds();
    }
    return true;
}

function drawMoldCard(card: moldCard, fees: Map<string, Promise<Big | undefined>>): HTMLElement {
    const $el = template.Clone('#ov-mold-template').firstElementChild as HTMLElement;
    const names = moldLabel(card.proof);
    ($el.querySelector('.ov-mold-label') as HTMLElement).textContent = names.label;
    $el.dataset.searchLabel = names.label;
    if (names.secondary) {
        const $what = $el.querySelector('.ov-mold-what') as HTMLElement;
        $what.textContent = names.secondary;
        $what.classList.remove('d-none');
    }
    // The mold grid reserves this column, so the tile always renders — with the
    // icon fallback when a mold somehow has no raster proof.
    const src = card.proof.main || card.proof.getURLs().main;
    fillArt($el.querySelector('.ov-mold-art'), ProofStack([{ src, alt: names.label }], { size: 'lg', enlarge: Boolean(src) }));
    const $dates = $el.querySelector('.ov-mold-dates') as HTMLElement;
    const twoSided = card.sides.length > 1;
    const moldDateTone = {
        expires: 'tag-secondary',
        lapsed: 'tag-danger',
        none: 'tag-light',
    };
    for (const side of card.sides) {
        const $pill = document.createElement('span');
        const tone = card.soon && side.status === 'expires' ? 'tag-warning' : moldDateTone[side.status];
        $pill.className = `ov-mold-date tag tag-soft tag-xs mb-0 font-family-filson-soft fw-bold ${tone}`;
        if (side.status === 'none') {
            $pill.textContent = i18n('moldNoMoldYet');
        } else {
            const date = side.date.toLocaleString(DateTime.DATE_MED);
            if (twoSided && side.side === 'front') {
                $pill.textContent = (side.status === 'lapsed' ? i18n('moldFrontLapsed') : i18n('moldFrontExpires')).replace('%s', date);
            } else if (twoSided && side.side === 'back') {
                $pill.textContent = (side.status === 'lapsed' ? i18n('moldBackLapsed') : i18n('moldBackExpires')).replace('%s', date);
            } else {
                $pill.textContent = (side.status === 'lapsed' ? i18n('moldLapsed') : i18n('moldExpires')).replace('%s', date);
            }
        }
        $dates.append($pill);
    }
    const lineItem = card.proof.latestOrderLineItem;
    const size = lineItem ? sizeOf(lineItem) : '';
    const $save = $el.querySelector('.ov-mold-save') as HTMLElement;
    // Render without the fee amount, then fill it in when the (deduped)
    // /mold-fees lookup resolves — awaiting it here serialized the whole
    // mold list behind one round trip per distinct product/type/size.
    $save.textContent = moldSaveSentence(card, undefined, Boolean(lineItem));
    if (lineItem?.lineItemModel?.name && lineItem.lineItemType?.name && size) {
        const key = `${lineItem.lineItemModel.name}|${lineItem.lineItemType.name}|${size}`;
        let pending = fees.get(key);
        if (!pending) {
            pending = GetMoldFees(lineItem.lineItemModel.name, lineItem.lineItemType.name, [size], 1).
                then(map => map.get(size)?.price).
                catch(() => undefined);
            fees.set(key, pending);
        }
        void pending.then(fee => {
            if (fee) {
                $save.textContent = moldSaveSentence(card, fee, true);
            }
        });
    }
    if (card.orders.length > 0) {
        const $orders = $el.querySelector('.ov-mold-orders') as HTMLElement;
        $orders.classList.remove('d-none');
        const newest = card.orders.reduce((a, b) => {
            if (!a.confirmedTime.isValid) {
                return b;
            }
            if (!b.confirmedTime.isValid) {
                return a;
            }
            return a.confirmedTime.toMillis() >= b.confirmedTime.toMillis() ? a : b;
        });
        const $wrap = document.createElement('span');
        const lastOrdered = newest.confirmedTime.isValid ?
            i18n('moldLastOrdered').replace('%s', newest.confirmedTime.toLocaleString(DateTime.DATE_MED)) :
            i18n('moldLastOrderedNoDate');
        $wrap.append(document.createTextNode(`${lastOrdered} · `));
        card.orders.forEach((order, i) => {
            if (i > 0) {
                $wrap.append(document.createTextNode(', '));
            }
            const $a = document.createElement('a');
            $a.href = `${Relref('/account/order')}?id=${order.id}`;
            $a.textContent = `#${order.invoiceNumber}`;
            $wrap.append($a);
        });
        $orders.replaceChildren($wrap);
        const $btn = $el.querySelector('.ov-mold-btn') as HTMLButtonElement;
        $btn.classList.remove('d-none');
        // The site's one reorder flow works per order (#6752 tracks per-design
        // targeting), so say which order this brings back.
        $btn.title = i18n('moldReorderTitle').replace('%s', String(newest.invoiceNumber));
        $btn.setAttribute('aria-label', i18n('moldReorderAria').replace('%s', String(newest.invoiceNumber)));
        $btn.addEventListener('click', async () => {
            Start($btn);
            await AddCartItem({ orderID: newest.id }).finally(() => Stop($btn));
        });
    }
    return $el;
}

// fee is the mold fee for the design's product/type/size once the lookup has
// resolved; undefined until then (or when there is no line item to price).
function moldSaveSentence(card: moldCard, fee: Big | undefined, hasLine: boolean): string {
    if (card.sides.some(side => side.status === 'none')) {
        return i18n('moldUnorderedQuote');
    }
    // One lapsed side is enough to re-tool — the savings sentence is only
    // true while every side is still live.
    const lapsed = card.sides.filter(side => side.status === 'lapsed');
    if (lapsed.length > 0) {
        const which = card.sides.length > 1 && lapsed.length < card.sides.length ? lapsed[0].side : '';
        const amount = hasLine && fee?.gt(0) ? DollarFormat(fee) : '';
        if (which === 'front') {
            return amount ? i18n('moldFeeAppliesAgainFrontFee').replace('%s', amount) : i18n('moldFeeAppliesAgainFront');
        }
        if (which === 'back') {
            return amount ? i18n('moldFeeAppliesAgainBackFee').replace('%s', amount) : i18n('moldFeeAppliesAgainBack');
        }
        return amount ? i18n('moldFeeAppliesAgainFee').replace('%s', amount) : i18n('moldFeeAppliesAgain');
    }
    if (!hasLine) {
        return '';
    }
    if (fee?.gt(0)) {
        return i18n('moldFeeStaysPaid').replace('%s', DollarFormat(fee));
    }
    return i18n('moldNoFee');
}

function drawSpend(orders: models.HyejooOrder[]): boolean {
    if (orders.length === 0) {
        return false;
    }
    const sum = orders.reduce((total, order) => total + order.total.toNumber(), 0);
    const $body = document.querySelector('#ov-spend-body') as HTMLElement;
    const $el = template.Clone('#ov-spend-template').firstElementChild as HTMLElement;
    ($el.querySelector('.ov-spend-total') as HTMLElement).textContent = DollarFormat(sum, false, true, 'USD', 0);
    ($el.querySelector('.ov-spend-orders') as HTMLElement).textContent = String(orders.length);
    ($el.querySelector('.ov-spend-avg') as HTMLElement).textContent = DollarFormat(sum / orders.length, false, true, 'USD', 0);
    const now = DateTime.now().startOf('month');
    const months: { key: string; label: string; v: number }[] = [];
    for (let i = 11; i >= 0; i--) {
        const m = now.minus({ months: i });
        months.push({ key: m.toFormat('yyyy-MM'), label: m.toFormat('LLLLL'), v: 0 });
    }
    const byKey = new Map(months.map(m => [m.key, m]));
    for (const order of orders) {
        if (!order.created.isValid) {
            continue;
        }
        const bucket = byKey.get(order.created.toFormat('yyyy-MM'));
        if (bucket) {
            bucket.v += order.total.toNumber();
        }
    }
    const max = Math.max(...months.map(m => m.v), 1);
    const $chart = $el.querySelector('.ov-spend-chart') as HTMLElement;
    for (const m of months) {
        const $col = template.Clone('#ov-spend-col-template').firstElementChild as HTMLElement;
        const $bar = $col.querySelector('.ov-spend-bar') as HTMLElement;
        const height = Math.max(m.v / max * 72, 3);
        $bar.style.height = `${height}px`;
        $bar.title = DollarFormat(m.v);
        if (m.v === 0) {
            $bar.classList.add('is-zero', 'bg-body-secondary');
        }
        ($col.querySelector('small') as HTMLElement).textContent = m.label;
        $chart.append($col);
    }
    $body.replaceChildren($el);
    return true;
}

function drawAttention(
    orders: models.HyejooOrder[],
    tickets: models.HyejooQuoteRequestProjectTask[],
    proofs: models.HyejooProof[],
    quotes: models.Quote[],
): boolean {
    const $list = document.querySelector('#ov-attention-list') as HTMLElement;
    const $count = document.querySelector('#ov-attention-count') as HTMLElement;
    $list.replaceChildren();
    const items: { icon: string; tone: 'primary' | 'secondary' | 'success' | 'danger' | 'warning'; title: string; meta: string; cta: string; href: string; art?: ProofItem[] }[] = [];
    for (const order of orders) {
        if (OrderStateOf(order)?.key !== 'awaiting-payment') {
            continue;
        }
        const due = DollarFormat(order.balance);
        items.push({
            art: OrderArt(order),
            icon: 'credit_card',
            tone: 'danger',
            title: `#${order.invoiceNumber} · ${productDescription(order)}`,
            meta: cardDeclined(order) ?
                i18n('accountCardDeclined').replace('%s', due) :
                i18n('accountDueWontStart').replace('%s', due),
            cta: i18n('accountPayNow'),
            href: `${Relref('/account/order')}?id=${order.id}`,
        });
    }
    for (const quote of quotes) {
        if (quote.quoteSents.length === 0 || quote.quoteOrders.length > 0) {
            continue;
        }
        const newest = quote.quoteSents.reduce((a, b) => a.created.toMillis() >= b.created.toMillis() ? a : b);
        const heading = quoteHeading(quote);
        const quoteArt: ProofItem[] = [];
        for (const entry of quote.quoteProofs) {
            const src = entry.proof?.getURLs().main ?? '';
            if (src) {
                quoteArt.push({ src, alt: heading });
            }
        }
        items.push({
            art: quoteArt,
            icon: 'request_quote',
            tone: 'primary',
            title: heading,
            meta: i18n('accountQuoteReady').
                replace('{total}', DollarFormat(quote.cachedTotal)).
                replace('{date}', newest.created.toLocaleString(DateTime.DATE_MED)),
            cta: i18n('accountOrderNow'),
            href: `${Relref('/account/quote')}?id=${quote.id}`,
        });
    }
    for (const task of tickets) {
        const last = task.lastUserEvent;
        // The hydrated event carries fromAdmin, never userID — an
        // admin-authored event is the designer speaking.
        if (!last?.id || !last.fromAdmin) {
            continue;
        }
        const name = task.quoteRequest?.quoteRequestProject?.name || i18n('timelineDesignTicket');
        // No needs-customer-response signal exists, so this only claims what
        // is known: the designer wrote last.
        items.push({
            art: TicketArt(task, name),
            icon: 'forum',
            tone: 'primary',
            title: name,
            meta: i18n('accountDesignerReplied').replace('%s', RelativeTime(last.created)),
            cta: i18n('accountReply'),
            href: `${Relref('/design-ticket')}?qrpt=${task.key}`,
        });
    }
    const soonCards = groupMolds(proofs).filter(card => card.soon);
    const soon = soonCards.length;
    if (soon > 0) {
        const moldArt: ProofItem[] = [];
        for (const card of soonCards) {
            const src = card.proof.main || card.proof.getURLs().main;
            if (src) {
                moldArt.push({ src, alt: moldLabel(card.proof).label });
            }
        }
        items.push({
            art: moldArt,
            icon: 'schedule',
            tone: 'warning',
            title: soon === 1 ? i18n('accountMoldExpiresOne') : i18n('accountMoldExpiresMany').replace('%s', String(soon)),
            meta: i18n('accountMoldExpiresMeta'),
            cta: i18n('accountSeeMolds'),
            href: '#ov-molds',
        });
    }
    for (const row of transitShipments(orders)) {
        if (!row.shipment.exceptionAt.isValid) {
            continue;
        }
        const carrier = row.shipment.carrier?.name || '';
        items.push({
            art: OrderArt(row.order),
            icon: 'error',
            tone: 'danger',
            title: i18n('accountDeliveryExceptionTitle').replace('{order}', String(row.order.invoiceNumber)).replace('{carrier}', carrier),
            meta: '',
            cta: i18n('accountTrack'),
            href: '#ov-shipments',
        });
    }
    if (items.length === 0) {
        return false;
    }
    $count.textContent = String(items.length);
    for (const item of items) {
        const $row = template.Clone('#ov-attention-template').firstElementChild as HTMLAnchorElement;
        $row.href = item.href;
        // The row itself is the link, so its picture is context, never a
        // control — no enlarge, or we would nest an anchor inside an anchor.
        const $art = ProofStack(item.art ?? [], { size: 'md', badge: item.icon, badgeTone: item.tone });
        fillArt($row.querySelector('.ov-att-art'), $art);
        const $icon = $row.querySelector('.ov-att-icon') as HTMLElement;
        if ($art) {
            $icon.remove();
        } else {
            $icon.classList.add(`icon-mark-${item.tone}`);
            ($icon.querySelector('.material-symbols-rounded') as HTMLElement).textContent = item.icon;
        }
        ($row.querySelector('b') as HTMLElement).textContent = item.title;
        $row.dataset.searchLabel = item.title;
        ($row.querySelector('small') as HTMLElement).textContent = item.meta;
        ($row.querySelector('.ov-att-cta-label') as HTMLElement).textContent = item.cta;
        $list.append($row);
    }
    return true;
}

function stampFeedDividers($target: HTMLElement) {
    $target.querySelectorAll('.ov-feed-divider').forEach(el => el.remove());
    const rows = Array.from($target.querySelectorAll<HTMLElement>('.ov-feed-row'));
    const newRows = rows.filter(row => row.classList.contains('is-new'));
    if (newRows.length === 0) {
        return;
    }
    const n = newRows.length;
    const lastIsNew = Boolean(rows[rows.length - 1]?.classList.contains('is-new'));
    const firstNew = newRows[0];
    let sinceVisit: string;
    if (lastIsNew) {
        sinceVisit = n === 1 ? i18n('accountNewSinceVisitOnePlus') : i18n('accountNewSinceVisitManyPlus').replace('%s', String(n));
    } else {
        sinceVisit = n === 1 ? i18n('accountNewSinceVisitOne') : i18n('accountNewSinceVisitMany').replace('%s', String(n));
    }
    firstNew.before(feedDivider(sinceVisit, true));
    const firstOld = rows.find((row, i) => !row.classList.contains('is-new') && rows.slice(0, i).some(r => r.classList.contains('is-new')));
    if (firstOld) {
        firstOld.before(feedDivider(i18n('accountEarlier'), false));
    }
}

function feedDivider(label: string, isNew: boolean): HTMLElement {
    const $el = template.Clone('#ov-feed-divider-template').firstElementChild as HTMLElement;
    if (isNew) {
        $el.classList.add('is-new');
    }
    ($el.querySelector('span') as HTMLElement).textContent = label;
    return $el;
}
